import { useEffect, useState, useMemo, useRef, Fragment, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { canonicalUrl, DEFAULT_OG_IMAGE } from "@/lib/site";
import { isAlbumReviewFormat } from "@/lib/utils";
import { ArrowRight, Play, ExternalLink, Disc3 } from "lucide-react";
import { api } from "@/lib/api";
import { resolveImageUrl, buildSrcSet } from "@/components/ImageUpload";
import { CoverImage } from "@/components/CoverImage";
import { RatingStars } from "@/components/RatingStars";
import { hasUpcomingDate, nextUpcomingDate, getEventDates } from "@/lib/events";
import { CarouselRow } from "@/components/CarouselRow";
import { HeroCarousel } from "@/components/HeroCarousel";
import { StoriesBar } from "@/components/StoriesBar";
import { prefetchArticle, prefetchSong } from "@/lib/prefetch";
import { Marquee } from "@/components/Marquee";
const Reels = lazy(() => import("@/components/Reels").then((m) => ({ default: m.Reels })));
import { InViewSection } from "@/components/InViewSection";
import { Asterisk, MusicNote, Star4 } from "@/components/UFO";

const DEFAULT_ORDER = ["hero", "recommendation", "featured_event", "songs", "playlists", "album_reviews", "agenda", "reels", "more_news"];

export default function Home() {
  const [articles, setArticles] = useState(() => (typeof window !== "undefined" && Array.isArray(window.__ESTROFA_ARTICLES__)) ? window.__ESTROFA_ARTICLES__ : []);
  const [events, setEvents] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [songs, setSongs] = useState([]);
  const [order, setOrder] = useState(DEFAULT_ORDER);

  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/playlists").then((r) => setPlaylists(r.data)).catch(() => {});
    api.get("/songs").then((r) => setSongs(r.data)).catch(() => {});
    api.get("/home/order").then((r) => { if (r.data?.order?.length) setOrder(r.data.order); }).catch(() => {});
  }, []);

  const [main, ...rest] = articles;
  const albumReviews = articles.filter((a) => isAlbumReviewFormat(a.format));
  const upcoming = events
    .filter(hasUpcomingDate)
    .sort((a, b) => new Date(nextUpcomingDate(a)) - new Date(nextUpcomingDate(b)));

  // Concierto de la Home: aleatorio de HOY; si no hay hoy, el próximo. (Sin destacar a mano.)
  const homePick = useMemo(() => {
    const t = new Date();
    const todayKey = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
    const todays = upcoming.filter((e) => getEventDates(e).includes(todayKey));
    if (todays.length) return { event: todays[Math.floor(Math.random() * todays.length)], isToday: true };
    const sorted = [...upcoming].sort((a, b) => new Date(nextUpcomingDate(a)) - new Date(nextUpcomingDate(b)));
    return { event: sorted[0] || null, isToday: false };
  }, [upcoming]);
  const featEvent = homePick.event;

  // Canción de la Home: aleatoria pero ESTABLE (una sola vez, sin parpadeo en StrictMode).
  const recIdRef = useRef(null);
  const rec = useMemo(() => {
    if (!songs.length) return null;
    if (!recIdRef.current || !songs.some((s) => s.id === recIdRef.current)) {
      recIdRef.current = songs[Math.floor(Math.random() * songs.length)].id;
    }
    return songs.find((s) => s.id === recIdRef.current) || null;
  }, [songs]);

  const blocks = {
    hero: (
      <HeroCarousel stories={articles.slice(0, 5)} />
    ),

    recommendation: rec && (
      <section className="relative bg-[#2A00D9] text-[#FAF9F6] overflow-hidden" data-testid="recommendation-section">
        <Star4 className="ufo absolute top-10 right-[6%] opacity-40" size={44} color="#B4D844" />
        <Asterisk className="ufo-slow absolute bottom-8 left-[5%] opacity-30" size={40} color="#B4D844" />
        <div className="mx-auto max-w-[1400px] section-pad grid md:grid-cols-12 gap-10 items-center min-h-[420px]">
          <div className="md:col-span-5 lg:col-span-4">
            <div className="relative group max-w-sm mx-auto md:mx-0">
              <div className="absolute -inset-3 rounded-full bg-[#B4D844]/20 blur-xl" aria-hidden />
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#1f00a3] shadow-2xl" data-testid="rec-cover">
                <CoverImage src={rec.cover_image} alt={rec.cover_image_alt || `${rec.song} — ${rec.artist}`} ratio="1/1" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <Disc3 className="absolute bottom-3 right-3 opacity-70 animate-[spin_6s_linear_infinite]" size={30} color="#B4D844" />
              </div>
              {rec.cover_credit && <p className="text-[11px] mt-2 text-right italic" style={{ color: "rgba(250,249,246,0.6)" }}>{rec.cover_credit}</p>}
            </div>
          </div>
          <div className="md:col-span-7 lg:col-span-8">
            <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] px-3 py-1 rounded-full bg-[#B4D844] text-[#1A1A1A] mb-5">
              <Star4 size={12} /> La canción del momento
            </p>
            <h2 className="display text-4xl sm:text-5xl lg:text-6xl leading-none" style={{ color: "#FAF9F6" }} data-testid="rec-song">{rec.song}</h2>
            <p className="display text-2xl italic mt-2" style={{ color: "#B4D844" }} data-testid="rec-artist">{rec.artist}</p>
            {rec.featured_reason && <p className="text-lg mt-6 max-w-2xl" style={{ color: "rgba(250,249,246,0.88)" }} data-testid="rec-reason">{rec.featured_reason}</p>}
            <div className="flex flex-wrap gap-3 mt-8">
              {rec.spotify_url && (
                <a href={rec.spotify_url} target="_blank" rel="noreferrer" className="btn-lime" data-testid="rec-link">
                  <Play size={16} /> Escuchar
                </a>
              )}
              {rec.slug && (
                <Link to={`/canciones/${rec.slug}`} className="btn-ghost" style={{ color: "#FAF9F6", borderColor: "rgba(250,249,246,0.4)" }} data-testid="rec-meaning-link">
                  Leer el significado <ArrowRight size={16} />
                </Link>
              )}
              <Link to="/canciones" className="inline-flex items-center gap-1.5 text-sm mt-2.5" style={{ color: "rgba(250,249,246,0.7)" }} data-testid="rec-history-link">
                Ver más canciones <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    ),

    featured_event: featEvent && (
      <section className="relative bg-[#FAF9F6] overflow-hidden border-y border-black/5" data-testid="featured-event-section">
        <MusicNote className="ufo absolute top-10 left-[4%] hidden md:block" size={40} color="#2A00D9" />
        <div className="mx-auto max-w-[1400px] section-pad grid md:grid-cols-12 gap-10 items-center min-h-[420px]">
          <div className="md:col-span-6 order-2 md:order-1">
            <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] px-3 py-1 rounded-full bg-[#2A00D9] text-[#FAF9F6] mb-5">
              <Star4 size={12} color="#B4D844" /> {homePick.isToday ? "Suena hoy en Madrid" : "Próximo concierto"}
            </p>
            <h2 className="display text-4xl sm:text-5xl lg:text-6xl leading-none text-[#1A1A1A]" data-testid="feat-event-title">{featEvent.title}</h2>
            {(featEvent.artists?.length ? featEvent.artists.join(" · ") : featEvent.artist) && <p className="display text-2xl italic mt-2 text-[#2A00D9]" data-testid="feat-event-artist">{featEvent.artists?.length ? featEvent.artists.join(" · ") : featEvent.artist}</p>}
            <div className="flex flex-wrap items-center gap-3 mt-5 text-sm text-[#4A4A4A]">
              <span className="inline-flex items-center gap-1.5 font-semibold text-[#1A1A1A]">
                <span className="h-2 w-2 rounded-full bg-[#B4D844]" />
                {new Date(nextUpcomingDate(featEvent)).toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "long" })}
              </span>
              <span>·</span>
              <span>{featEvent.venue}, {featEvent.city}</span>
              {featEvent.genre && <span className="eyebrow text-[10px] bg-[#B4D844]/40 text-[#1A1A1A] px-2 py-0.5 rounded-full">{featEvent.genre}</span>}
            </div>
            {featEvent.featured_reason && <p className="text-lg mt-6 max-w-2xl text-[#4A4A4A]" data-testid="feat-event-reason">{featEvent.featured_reason}</p>}
            <div className="flex flex-wrap gap-3 mt-8">
              {featEvent.ticket_url && (
                <a href={featEvent.ticket_url} target="_blank" rel="noreferrer" className="btn-cobalt" data-testid="feat-event-tickets">
                  Entradas <ExternalLink size={16} />
                </a>
              )}
              {featEvent.spotify_url && (
                <a href={featEvent.spotify_url} target="_blank" rel="noreferrer" className="btn-lime" data-testid="feat-event-spotify">
                  <Play size={16} /> Escuchar
                </a>
              )}
              <Link to="/agenda" className="btn-ghost" data-testid="feat-event-agenda">
                Ver la agenda <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <div className="relative group max-w-lg mx-auto md:ml-auto">
              <div className="absolute -top-4 -right-4 w-1/2 h-2/3 bg-[#B4D844] rounded-2xl -z-0" aria-hidden />
              <div className="absolute -bottom-3 -left-3 w-20 h-20 bg-[#2A00D9] rounded-2xl -z-0" aria-hidden />
              <div className="relative z-10 aspect-[16/11] rounded-2xl overflow-hidden bg-[#F3F2EE] shadow-2xl" data-testid="feat-event-cover">
                <CoverImage src={featEvent.image} alt={featEvent.image_alt || featEvent.title} ratio="16/11" testid="feat-event-cover-img" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              {featEvent.image_credit && <p className="text-[11px] mt-2 text-right italic text-[#4A4A4A]">{featEvent.image_credit}</p>}
            </div>
          </div>
        </div>
      </section>
    ),

    songs: songs.length > 0 && (
      <CarouselRow eyebrow="Letras" title="Significado de canciones" to="/canciones" linkLabel="Todas" testid="row-canciones">
        {songs.slice(0, 10).map((s) => (
          <Link key={s.id} to={`/canciones/${s.slug}`} onMouseEnter={() => prefetchSong(s.slug)} onFocus={() => prefetchSong(s.slug)} className="snap-start shrink-0 w-[220px] group bg-white rounded-2xl p-3 shadow-sm hover:shadow-lg transition-shadow" data-testid={`song-card-${s.id}`}>
            <div className="aspect-square rounded-xl overflow-hidden bg-[#F3F2EE] mb-3">
              <CoverImage src={s.cover_image} alt={s.cover_image_alt || s.song} ratio="1/1" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <h3 className="display text-lg leading-tight group-hover:text-[#2A00D9] transition-colors line-clamp-1">{s.song}</h3>
            <p className="text-sm text-[#4A4A4A] italic line-clamp-1">{s.artist}</p>
          </Link>
        ))}
      </CarouselRow>
    ),

    playlists: playlists.length > 0 && (
      <CarouselRow eyebrow="Banda sonora" title="Playlists" to="/playlists" linkLabel="Todas" testid="row-colecciones">
        {playlists.slice(0, 10).map((p) => (
          <Link key={p.id} to={p.slug ? `/playlists/${p.slug}` : "/playlists"} className="snap-start shrink-0 w-[220px] group bg-white rounded-2xl p-3 shadow-sm hover:shadow-lg transition-shadow" data-testid={`col-card-${p.id}`}>
            <div className="aspect-square rounded-xl overflow-hidden bg-[#F3F2EE] mb-3 relative">
              <CoverImage src={p.cover_image} alt={p.cover_image_alt || p.title} ratio="1/1" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute bottom-3 right-3 h-10 w-10 rounded-full bg-[#B4D844] text-[#1A1A1A] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"><Play size={16} /></span>
            </div>
            <p className="eyebrow text-[10px] mb-1">{p.category || p.mood}</p>
            <h3 className="display text-lg leading-tight group-hover:text-[#2A00D9] transition-colors line-clamp-2">{p.title}</h3>
          </Link>
        ))}
      </CarouselRow>
    ),

    album_reviews: albumReviews.length > 0 && (
      <InViewSection minHeight={520} rootMargin="150px" testid="album-reviews-defer">
      <section className="relative overflow-hidden bg-[#141414] text-[#FAF9F6]" data-testid="home-album-reviews-section">
        <Disc3 className="ufo-slow absolute top-10 right-[5%] opacity-10 hidden md:block" size={120} color="#B4D844" />
        <div className="relative mx-auto max-w-[1200px] section-pad text-center">
          <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-[#B4D844] mb-4" data-testid="album-reviews-eyebrow">
            <Disc3 size={14} /> Disco a disco
          </p>
          <h2 className="display text-4xl sm:text-5xl lg:text-6xl leading-none mb-4" style={{ color: "#fff" }} data-testid="album-reviews-title">Análisis de discos</h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto mb-12">Nuestras reseñas a fondo de los álbumes que marcan la temporada.</p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-16">
            {albumReviews.slice(0, 3).map((a) => (
              <Link key={a.id} to={`/noticias/${a.slug}`} onMouseEnter={() => prefetchArticle(a.slug)} onFocus={() => prefetchArticle(a.slug)} className="sleeve-link group flex flex-col items-center outline-none" data-testid={`album-review-card-${a.id}`}>
                <div className="sleeve-scene">
                  <div className="sleeve-wrap">
                    <div className="sleeve-record" aria-hidden="true">
                      <div className="sleeve-record-rotor">
                        <div className="sleeve-record-label">
                          <CoverImage src={a.cover_image} alt="" ratio="1/1" className="w-full h-full object-cover" />
                        </div>
                        <span className="sleeve-record-hole" />
                      </div>
                    </div>
                    <div className="sleeve-cover">
                      <CoverImage src={a.cover_image} alt={a.cover_image_alt || a.title} ratio="1/1" className="w-full h-full object-cover" testid={`album-review-cover-${a.id}`} />
                    </div>
                  </div>
                </div>
                <div className="mt-7 text-center max-w-[260px]">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#B4D844] mb-2">Análisis de disco</p>
                  <h3 className="display text-xl leading-tight mb-2 group-hover:text-[#B4D844] transition-colors line-clamp-2" style={{ color: "#fff" }}>{a.title}</h3>
                  {a.rating != null && (
                    <RatingStars rating={a.rating} size={16} emptyClassName="text-white/20" valueClassName="text-white" className="justify-center mb-1" testid={`album-review-rating-${a.id}`} />
                  )}
                  <p className="text-xs text-white/40 italic mt-2">Por {a.author}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/noticias" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold border border-white/25 text-white hover:bg-white/10 transition" data-testid="album-reviews-all">
              Ver todos los análisis <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
      </InViewSection>
    ),

    agenda: upcoming.length > 0 && (
      <section data-testid="row-agenda-wrap">
        <CarouselRow
          eyebrow="Madrid · Esta temporada"
          title="Próximos conciertos"
          to="/agenda"
          linkLabel="Agenda"
          testid="row-agenda"
        >
          {upcoming.slice(0, 10).map((e) => (
            <Link key={e.id} to="/agenda" className="snap-start shrink-0 w-[300px] card-paper overflow-hidden group" data-testid={`agenda-card-${e.id}`}>
              <div className="aspect-[16/10] bg-[#F3F2EE] overflow-hidden relative">
                <CoverImage src={e.image} alt={e.image_alt || e.title} ratio="16/10" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute top-3 left-3 bg-[#B4D844] text-[#1A1A1A] text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full">
                  {new Date(nextUpcomingDate(e)).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}
                </span>
              </div>
              <div className="p-5">
                <p className="eyebrow text-[10px] mb-1">{e.genre}</p>
                <h3 className="display text-xl leading-tight mb-1">{e.title}</h3>
                <p className="text-sm text-[#4A4A4A]">{e.venue}, {e.city}</p>
              </div>
            </Link>
          ))}
        </CarouselRow>
      </section>
    ),

    reels: <InViewSection key="reels" minHeight={460} rootMargin="150px" testid="reels-defer"><Suspense fallback={null}><Reels /></Suspense></InViewSection>,

    more_news: rest.length > 0 && (
      <CarouselRow eyebrow="El sumario" title="Más noticias" to="/noticias" linkLabel="Todas" testid="row-noticias">
        {rest.slice(0, 10).map((a) => (
          <Link key={a.id} to={`/noticias/${a.slug}`} onMouseEnter={() => prefetchArticle(a.slug)} onFocus={() => prefetchArticle(a.slug)} className="snap-start shrink-0 w-[260px] group bg-white rounded-2xl p-3 shadow-sm hover:shadow-lg transition-shadow" data-testid={`news-card-${a.id}`}>
            <div className="aspect-[4/5] rounded-xl overflow-hidden bg-[#F3F2EE] mb-3">
              <CoverImage src={a.cover_image} alt={a.cover_image_alt || a.title} ratio="4/5" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <p className="eyebrow text-[10px] mb-1">{a.format || "Noticia"}</p>
            <h3 className="display text-xl leading-tight group-hover:text-[#2A00D9] transition-colors line-clamp-2">{a.title}</h3>
          </Link>
        ))}
      </CarouselRow>
    ),
  };

  const orderedKeys = [...order.filter((k) => blocks[k] !== undefined), ...DEFAULT_ORDER.filter((k) => !order.includes(k))].filter((k) => k !== "radar");
  return (
    <div data-testid="page-home">
      <Helmet>
        <title>La Estrofa · Actualidad musical, lanzamientos y entrevistas</title>
        <meta name="description" content="La Estrofa, medio digital de comunicación musical: actualidad, lanzamientos y entrevistas a artistas. Agenda de conciertos en Madrid, playlists y significado de canciones." />
        <link rel="canonical" href={canonicalUrl("/")} />
        {main?.cover_image && (
          <link rel="preload" as="image" href={resolveImageUrl(main.cover_image)} imageSrcSet={buildSrcSet(main.cover_image)} imageSizes="(max-width: 768px) 92vw, 58vw" fetchPriority="high" />
        )}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="La Estrofa · Actualidad musical, lanzamientos y entrevistas" />
        <meta property="og:description" content="Medio digital de comunicación musical: actualidad, lanzamientos y entrevistas. Agenda de conciertos en Madrid, playlists y significado de canciones." />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="og:url" content={canonicalUrl("/")} />
        <meta name="twitter:title" content="La Estrofa · Actualidad musical, lanzamientos y entrevistas" />
        <meta name="twitter:description" content="Medio digital de comunicación musical: actualidad, lanzamientos y entrevistas." />
        <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      </Helmet>
      <StoriesBar />
      {orderedKeys.map((k) => (
        <Fragment key={k}>{blocks[k]}</Fragment>
      ))}
      <Marquee />
    </div>
  );
}
